import React, { useState } from 'react';
import { 
  User, 
  CreditCard, 
  ShieldAlert, 
  Trash2, 
  ArrowLeft, 
  Loader2,
  AlertCircle,
  Bell,
  Zap,
  Shield
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db, doc, updateDoc, serverTimestamp, addDoc, collection, deleteDoc } from '../firebase';
import { toast, Toaster } from 'sonner';
import { motion, AnimatePresence } from 'motion/react';
import { trackUserAction } from '../lib/events';
import { createPortalSession } from '../lib/stripe';

export default function Settings({ profile }: { profile: any }) {
  const [activeSegment, setActiveSegment] = useState<'profile' | 'security' | 'billing' | 'notifications'>('profile');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleUpdate = (type: string) => {
    if (!auth.currentUser) return;
    trackUserAction(auth.currentUser.uid, 'settings_updated', `Updated ${type} settings`, 'System');
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} settings updated`);
  };

  const handleManageBilling = async () => {
    if (!auth.currentUser) return;
    const toastId = toast.loading("Accessing billing portal...");
    try {
      await createPortalSession(auth.currentUser.uid);
    } catch (error: any) {
      console.error("Portal error:", error);
      toast.error(error.message || "Failed to open billing portal.", { id: toastId });
    }
  };

  const handleCancelSubscription = async () => {
    // We now prefer the Stripe Portal
    handleManageBilling();
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    const toastId = toast.loading("Processing account deletion...");
    try {
      await addDoc(collection(db, 'support_tickets'), {
        uid: auth.currentUser?.uid,
        email: auth.currentUser?.email,
        type: 'account_deletion',
        status: 'pending',
        message: 'User requested permanent account deletion.',
        createdAt: serverTimestamp()
      });
      trackUserAction(auth.currentUser?.uid || '', 'account_delete_requested', `Requested permanent account deletion`, 'System');
      toast.success("Deletion request received. Your account will be purged within 48 hours.", { id: toastId });
      setShowDeleteConfirm(false);
    } catch (error) {
      toast.error("Failed to process request.", { id: toastId });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex h-screen bg-[#050505] text-white">
      <Toaster position="top-right" theme="dark" />
      
      {/* Settings Navigation Sidebar */}
      <aside className="w-64 border-r border-[#1a1a1a] flex flex-col pt-8 bg-[#050505] z-10 shrink-0">
        <div className="px-6 mb-8 flex items-center gap-3">
          <Link 
            to="/studio"
            className="w-8 h-8 rounded-lg bg-[#111111] border border-[#222222] flex items-center justify-center text-zinc-400 hover:text-white transition-all"
          >
            <ArrowLeft size={16} />
          </Link>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">Laboratory</span>
        </div>

        <nav className="flex-1 space-y-1 px-3">
          {[
            { id: 'profile', label: 'User Profile', icon: User },
            { id: 'billing', label: 'Capital & Plans', icon: CreditCard },
            { id: 'security', label: 'Shield & Privacy', icon: Shield },
            { id: 'notifications', label: 'Telemetry Alerts', icon: Bell },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSegment(item.id as any)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                activeSegment === item.id 
                  ? 'bg-[#111111] text-white border border-[#222222]' 
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-[#111111]/50'
              }`}
            >
              <item.icon size={18} className={activeSegment === item.id ? 'text-[#0074D9]' : 'text-zinc-600 group-hover:text-zinc-400'} />
              <span className="text-xs font-sans font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-6 border-t border-[#1a1a1a]">
          <button 
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#FF4444] hover:bg-[#FF4444]/5 rounded-xl transition-all text-xs font-medium"
          >
            <Trash2 size={16} />
            Purge Data
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto bg-[#050505]">
        <div className="max-w-3xl mx-auto p-12 py-16">
          <header className="mb-12">
            <h1 className="text-2xl font-sans font-medium text-white mb-2">
              {activeSegment === 'profile' && 'Account Identity'}
              {activeSegment === 'billing' && 'Capital & Distribution'}
              {activeSegment === 'security' && 'Hardening & Privacy'}
              {activeSegment === 'notifications' && 'Telemetry Alerts'}
            </h1>
            <p className="text-sm text-zinc-500 font-sans">
              Manage your global identity and platform preferences.
            </p>
          </header>

          <div className="space-y-8">
            {activeSegment === 'profile' && (
              <div className="space-y-6">
                <div className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-2xl p-8 space-y-8">
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-2xl bg-[#111111] border border-[#222222] flex items-center justify-center relative group">
                      <User size={32} className="text-zinc-700" />
                      <button className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all rounded-2xl">
                        <span className="text-[9px] font-mono uppercase text-white">Update</span>
                      </button>
                    </div>
                    <div>
                      <h3 className="text-sm font-sans font-medium text-white">{auth.currentUser?.email}</h3>
                      <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mt-1 italic">
                        UID: {auth.currentUser?.uid.slice(0, 16)}...
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">Public Alias</label>
                      <input 
                        type="text" 
                        placeholder={auth.currentUser?.displayName || 'Alpha Builder'}
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-xs text-white focus:border-zinc-600 transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">Platform Designation</label>
                      <input 
                        type="text" 
                        placeholder="Founder / Architect"
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-xs text-white focus:border-zinc-600 transition-all outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button 
                    onClick={() => handleUpdate('profile')}
                    className="px-6 py-3 bg-white text-black text-[10px] font-mono font-bold uppercase rounded-xl hover:bg-zinc-200 transition-all"
                  >
                    Update Identity
                  </button>
                </div>
              </div>
            )}

            {activeSegment === 'billing' && (
              <div className="space-y-6">
                <div className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-2xl p-8">
                  <div className="flex justify-between items-start mb-12">
                    <div>
                      <h3 className="text-lg font-sans font-medium text-white mb-1">
                        {profile?.isPro ? (profile?.isFounder ? 'Founder Access' : 'Pro Laboratory') : 'Standard Access'}
                      </h3>
                      <p className="text-xs text-zinc-500 font-sans">
                        {profile?.isPro ? 'Priority bandwidth & unlimited synthesis enabled.' : 'Upgrade for unlimited synthesis and Kinetics engine.'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                       <span className={`px-3 py-1 text-[9px] font-mono uppercase rounded-full border ${profile?.isPro ? 'bg-green-950/30 text-green-500 border-green-900/50' : 'bg-zinc-900 text-zinc-500 border-zinc-800'}`}>
                        {profile?.isPro ? 'Active' : 'Limited'}
                      </span>
                      {profile?.isPro && (
                        <p className="text-[10px] text-zinc-600 font-mono uppercase">
                          {profile?.isFounder ? '$29 First Month / $99 Regular' : '$99 / Month'}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    <div className="p-4 bg-[#111111] border border-[#222222] rounded-xl">
                      <p className="text-[9px] font-mono uppercase text-zinc-500 mb-1">Compute Credits</p>
                      <p className="text-xl font-sans font-medium text-white">{profile?.isPro ? 'Unlimited' : '10 / Month'}</p>
                    </div>
                    <div className="p-4 bg-[#111111] border border-[#222222] rounded-xl">
                      <p className="text-[9px] font-mono uppercase text-zinc-500 mb-1">Active Projects</p>
                      <p className="text-xl font-sans font-medium text-white">{profile?.isPro ? 'Unlimited' : '3 Max'}</p>
                    </div>
                  </div>

                  {profile?.isPro ? (
                    <button 
                      onClick={handleManageBilling}
                      className="w-full py-4 border border-[#222222] text-zinc-400 hover:text-white hover:border-zinc-700 rounded-xl text-[10px] font-mono uppercase font-bold transition-all flex items-center justify-center gap-2"
                    >
                      <CreditCard size={14} />
                      Manage Billing & Plan
                    </button>
                  ) : (
                    <Link 
                      to="/studio?upgrade=true"
                      className="w-full py-4 bg-[#0074D9] text-white flex items-center justify-center gap-2 rounded-xl text-[10px] font-mono uppercase font-bold hover:bg-[#0063b8] transition-all"
                    >
                      <Zap size={14} />
                      Ignite Pro Access — $99/mo
                    </Link>
                  )}
                </div>
              </div>
            )}

            {activeSegment === 'security' && (
              <div className="space-y-6">
                <div className="bg-[#0A0A0A] border border-[#1a1a1a] rounded-2xl p-8">
                  <h3 className="text-sm font-sans font-medium text-white mb-6">Security Configuration</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-[#111111] border border-[#222222] rounded-xl">
                      <div className="flex items-center gap-4">
                        <ShieldAlert size={20} className="text-zinc-500" />
                        <div>
                          <p className="text-xs font-sans font-medium text-white">Two-Factor Authentication</p>
                          <p className="text-[10px] text-zinc-500 font-sans">Requires a physical device for login approval.</p>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 bg-zinc-900 border border-zinc-800 text-zinc-600 text-[8px] font-mono uppercase rounded">Locked</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeSegment === 'notifications' && (
              <div className="py-20 text-center bg-[#0A0A0A] border border-[#1a1a1a] rounded-2xl border-dashed">
                <Bell size={32} className="mx-auto text-zinc-800 mb-4" />
                <p className="text-[10px] font-mono uppercase text-zinc-500 tracking-widest italic">Alert Engine Disengaged</p>
                <p className="text-xs text-zinc-700 font-sans mt-2">Global telemetry alerts are configured via CLI or Discord webhooks.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-[#050505] border border-[#222222] p-12 rounded-3xl max-w-md w-full text-center space-y-8"
            >
              <div className="w-20 h-20 bg-[#FF4444]/10 rounded-2xl flex items-center justify-center mx-auto text-[#FF4444] border border-[#FF4444]/20">
                <AlertCircle size={40} />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-sans font-medium text-white">Purge Laboratory?</h3>
                <p className="text-[#888888] font-light text-sm leading-relaxed">
                  This will send a permanent deletion request to the core team. All your projects, blueprints, and kinetics data will be wiped from existence.
                </p>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="w-full py-4 bg-[#FF4444] text-white rounded-xl text-xs font-bold uppercase tracking-widest transition-all hover:bg-[#CC0000] disabled:opacity-50"
                >
                  {isDeleting ? 'Processing...' : 'Purge Everything'}
                </button>
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="w-full py-4 text-zinc-500 hover:text-white transition-all text-xs font-mono uppercase"
                >
                  Retreat
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
